import {
  DEFAULT_BLOTTER_SELECTION_READONLY_PARAMS,
  useAccordionFilterBuilder,
  useBlotterGroupingToggling,
  useBlotterTable,
  useCallbackRef,
  usePersistedBlotterTable,
  useWSFilterPipe,
} from '@talos/kyoko';
import type { StateUpdatedEvent } from 'ag-grid-community';
import { useMemo } from 'react';
import { defaultUnifiedBlotterColumns, defaultUnifiedBlotterFilter } from '../../../../Blotters/PositionsV3/defaults';
import type { UnifiedPositionsTabProps } from '../../../../Blotters/PositionsV3/types';
import { POSITIONS_UNIFIED_GROUPINGS } from '../../../../Blotters/PositionsV3/Unified/positionsUnifiedGroupings';
import type { UnifiedPosition } from '../../../../Blotters/PositionsV3/Unified/UnifiedPosition';
import { usePositionsUnifiedColumns } from '../../../../Blotters/PositionsV3/Unified/usePositionsUnifiedColumns';
import { usePositionsUnifiedFilter } from '../../../../Blotters/PositionsV3/Unified/usePositionsUnifiedFilter';
import { useUnifiedPositionsBalancesObs } from '../../../../Blotters/PositionsV3/Unified/useUnifiedPositionsBalancesObs';
import { useUnifiedPositionsClientFilter } from '../../../../Blotters/PositionsV3/Unified/useUnifiedPositionsClientFilter';
import { MobileBlotterToolbar } from '../MobileBlotterToolbar';
import { MobilePositionsSettingsToggles } from './MobilePositionsSettingsToggles';
import { MobilePositionsBlotterView, getPositionKey } from './sharedPositionsLogic';

interface MobilePositionsUnifiedProps {
  blotterID: string;
  tab: UnifiedPositionsTabProps;
  onUpdateTab: (tab: UnifiedPositionsTabProps) => void;
}

/**
 * Mobile counterpart to desktop `PositionsUnifiedBlotter`. Uses the unified
 * positions balances observable directly (same as desktop) — not a WebSocket
 * subscription — and overlays the same client-side filter pipeline.
 */
export function MobilePositionsUnified({ blotterID, tab, onUpdateTab }: MobilePositionsUnifiedProps) {
  const defaultColumns = tab.defaultColumns ?? defaultUnifiedBlotterColumns;
  const defaultFilter = tab.defaultFilter ?? defaultUnifiedBlotterFilter;
  const showZeroBalances = tab.showZeroBalances ?? false;
  const includeCash = tab.includeCash ?? true;

  const columns = usePositionsUnifiedColumns(defaultColumns);
  const persistedBlotterTable = usePersistedBlotterTable(blotterID, { filter: defaultFilter });
  const filterResults = usePositionsUnifiedFilter({ persistedBlotterTable, defaultFilter });

  const rxjsFilterFunc = useUnifiedPositionsClientFilter({
    filter: filterResults.filter,
    includeCash,
  });

  const filterPipe = useWSFilterPipe({ getUniqueKey: getPositionKey, filterFunc: rxjsFilterFunc });

  const { dataObservable, errorObservable } = useUnifiedPositionsBalancesObs({
    tag: blotterID,
    showZeroBalances,
    filter: filterResults.filter,
  });

  const filteredDataObservable = useMemo(() => dataObservable.pipe(filterPipe), [dataObservable, filterPipe]);

  const handleGridStateUpdated = useCallbackRef((event: StateUpdatedEvent) => {
    onGroupingStateUpdated(event);
  });

  const blotterTable = useBlotterTable<UnifiedPosition>({
    dataObservable: filteredDataObservable,
    errorObservable,
    rowID: 'rowID',
    persistence: persistedBlotterTable,
    columns,
    gridOptions: {
      onStateUpdated: handleGridStateUpdated,
      showOpenedGroup: true,
      groupHideParentOfSingleChild: 'leafGroupsOnly',
      groupDisplayType: 'singleColumn',
      rowSelection: DEFAULT_BLOTTER_SELECTION_READONLY_PARAMS,
    },
    exportOptions: { name: 'PositionsUnified', tabLabel: tab.label },
  });

  const { onStateUpdated: onGroupingStateUpdated, groupingToggleButtons } = useBlotterGroupingToggling({
    blotterName: 'positions unified',
    setRowGroupColumns: blotterTable.setRowGroupColumns,
    groupings: POSITIONS_UNIFIED_GROUPINGS,
    allowNoGrouping: true,
    useMobileLabels: true,
    wrap: true,
  });

  const filterBuilderAccordion = useAccordionFilterBuilder({
    filterBuilderProps: filterResults.filterBuilderProps,
  });

  return (
    <>
      <MobileBlotterToolbar
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        showDateRangePicker={false}
        accordionBodyPrefix={groupingToggleButtons}
        settingsContent={<MobilePositionsSettingsToggles tab={tab} onUpdateTab={onUpdateTab} />}
      />
      <MobilePositionsBlotterView<UnifiedPosition> blotterTable={blotterTable} />
    </>
  );
}
